// lib/realtime/RealtimeManager.ts
import { supabaseClient } from "../supabase/client";
import type { RealtimeChannel, RealtimePostgresChangesPayload } from "@supabase/supabase-js";

type OnTriggerCallback = (payload: RealtimePostgresChangesPayload<{ [key: string]: unknown }>) => void;

class RealtimeSubscriptionManager {
  private requests = new Map<
    string,
    { tables: Set<string>; onTrigger?: OnTriggerCallback }
  >();
  private universalChannel: RealtimeChannel | null = null;

  private opQueue: (() => void)[] = [];
  private processing = false;

  // --- ADDED: Timer for debouncing ---
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly DEBOUNCE_MS = 1000; // Wait 1 second after the last message to refresh

  private enqueue(op: () => void) {
    this.opQueue.push(op);
    if (!this.processing) {
      this.processing = true;
      Promise.resolve().then(() => this.flushQueue());
    }
  }

  private flushQueue() {
    while (this.opQueue.length > 0) {
      const op = this.opQueue.shift();
      if (op) op();
    }
    this.processing = false;
  }

  public add(
    componentId: string,
    tables: string[],
    onTrigger?: OnTriggerCallback
  ) {
    this.enqueue(() => {
      this.requests.set(componentId, { tables: new Set(tables), onTrigger });
      this.ensureSubscription();
    });
  }

  public remove(componentId: string) {
    this.enqueue(() => {
      this.requests.delete(componentId);
      if (this.requests.size === 0) {
        this.teardownChannel();
      }
    });
  }

  public getRequiredTables(): string[] {
    const required = new Set<string>();
    this.requests.forEach(({ tables }) => {
      tables.forEach((t) => required.add(t));
    });
    return Array.from(required).sort();
  }

  // --- ADDED: Helper to trigger the refresh ---
  private triggerRefresh() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    // Set a new timer. The actual window event will only fire
    // if no new messages come in for 1000ms.
    this.debounceTimer = setTimeout(() => {
      console.log("[RealtimeManager] Debounce complete. Triggering Plasmic Refresh.");
      window.dispatchEvent(new Event("plasmic_refresh_all"));
      this.debounceTimer = null;
    }, this.DEBOUNCE_MS);
  }

  private ensureSubscription() {
    if (this.universalChannel) return;

    this.universalChannel = supabaseClient
      .channel("__universal-channel__")
      .on("postgres_changes", { event: "*", schema: "public" }, (payload: RealtimePostgresChangesPayload<{ [key: string]: unknown }>) => {
        const changedTable = payload.table;
        let refreshNeeded = false;
        
        this.requests.forEach(({ tables, onTrigger }) => {
          if (tables.has(changedTable)) {
            // NOTE: We still fire specific callbacks immediately. 
            // If onTrigger does heavy lifting, you might want to debounce this too, 
            // but usually this is for granular UI updates (like toasts).
            if (onTrigger) onTrigger(payload);
            
            refreshNeeded = true;
          }
        });

        if (refreshNeeded) {
          // --- UPDATED: Call the debounce helper instead of dispatching immediately ---
          this.triggerRefresh();
        }
      })
      .subscribe();
  }

  private teardownChannel() {
    if (this.universalChannel) {
      supabaseClient.removeChannel(this.universalChannel);
      this.universalChannel = null;
    }
  }

  public cleanup() {
    // FIX: Avoid supabaseClient.removeAllChannels() which triggers the iterable bug.
    // Instead, explicitly tear down just the channel this manager handles.
    this.teardownChannel();
    
    this.requests.clear();
    
    // Clear any pending timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }
}

type AppGlobal = typeof globalThis & {
  __subscriptionManager?: RealtimeSubscriptionManager;
};

// HMR‑safe singleton
const g = globalThis as AppGlobal;
export const subscriptionManager: RealtimeSubscriptionManager =
  g.__subscriptionManager || (g.__subscriptionManager = new RealtimeSubscriptionManager());